import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Phone, Mail, MapPin, Clock, MessageCircle, Send } from 'lucide-react';
import { contactInfo } from '../data/mock';

const trackWhatsAppConversion = () => {
  if (window.gtag) {
    window.gtag('event', 'conversion', {
      send_to: 'AW-18163572112/onMQCKnajq0cEJC7iNVD',
      value: 1.0,
      currency: 'MXN'
    });
  }
};

export const Contact = () => {
  // Contact Form State
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  // Quote Form State
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    productType: '',
    quantity: '',
    description: ''
  });

  // Check if product was selected from Products section
  useEffect(() => {
    const selectedProduct = sessionStorage.getItem('selectedProduct');
    if (selectedProduct) {
      setQuoteForm(prev => ({ ...prev, productType: selectedProduct }));
      sessionStorage.removeItem('selectedProduct');
    }
  }, []);

  const handleContactSubmit = (e) => {
    e.preventDefault();
    const message = encodeURIComponent(
      `Hola, me pongo en contacto desde su página web.\n\n` +
      `Nombre: ${contactForm.name}\n` +
      `Email: ${contactForm.email}\n` +
      `Teléfono: ${contactForm.phone}\n\n` +
      `Mensaje: ${contactForm.message}`
    );
    trackWhatsAppConversion();
    window.open(`https://wa.me/${contactInfo.whatsapp}?text=${message}`, '_blank');
    setContactForm({ name: '', email: '', phone: '', message: '' });
  };

  const handleQuoteSubmit = (e) => {
    e.preventDefault();
    const message = encodeURIComponent(
      `Hola, quisiera solicitar una cotización.\n\n` +
      `Nombre: ${quoteForm.name}\n` +
      `Empresa: ${quoteForm.company}\n` +
      `Email: ${quoteForm.email}\n` +
      `Teléfono: ${quoteForm.phone}\n` +
      `Tipo de producto: ${quoteForm.productType}\n` +
      `Cantidad: ${quoteForm.quantity}\n\n` +
      `Descripción: ${quoteForm.description}`
    );
    trackWhatsAppConversion();
    window.open(`https://wa.me/${contactInfo.whatsapp}?text=${message}`, '_blank');
    setQuoteForm({ name: '', company: '', email: '', phone: '', productType: '', quantity: '', description: '' });
  };

  const handleWhatsApp = () => {
    const message = encodeURIComponent("Hola, me interesa conocer más sobre sus productos de tarimas de madera.");
    trackWhatsAppConversion();
    window.open(`https://wa.me/${contactInfo.whatsapp.replace(/\s+/g, '')}?text=${message}`, '_blank');
  };

  return (
    <section id="contacto" className="py-20 bg-gradient-to-b from-white to-green-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
            <span className="text-green-700">Contáctanos</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Estamos listos para atender tus necesidades de embalaje y logística
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Contact Info Cards */}
          <div className="lg:col-span-1 space-y-6">
            <Card className="border-2 border-green-100 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Teléfono</h3>
                    <a href={`tel:${contactInfo.phone}`} className="text-green-700 hover:text-green-800">
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-100 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">WhatsApp</h3>
                    <button 
                      onClick={handleWhatsApp}
                      className="text-green-700 hover:text-green-800"
                    >
                      {contactInfo.whatsapp}
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-100 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Email</h3>
                    <a href={`mailto:${contactInfo.email}`} className="text-green-700 hover:text-green-800 break-all">
                      {contactInfo.email}
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-100 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Ubicación</h3>
                    <p className="text-gray-600">{contactInfo.address}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-2 border-green-100 hover:shadow-lg transition-shadow">
              <CardContent className="pt-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Horario</h3>
                    <p className="text-gray-600 text-sm">{contactInfo.hours}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* WhatsApp CTA */}
            <Button 
              onClick={handleWhatsApp}
              className="w-full bg-green-600 hover:bg-green-700 text-white py-6 text-lg"
            >
              <MessageCircle className="w-5 h-5 mr-2" />
              Chatear por WhatsApp
            </Button>
          </div>

          {/* Contact & Quote Forms */}
          <div className="lg:col-span-2">
            <Card className="border-2 border-green-100">
              <CardHeader>
                <CardTitle className="text-2xl">Envíanos un mensaje</CardTitle>
                <CardDescription>
                  Completa el formulario y nos pondremos en contacto contigo lo antes posible
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="contacto" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-6">
                    <TabsTrigger value="contacto" className="data-[state=active]:bg-green-700 data-[state=active]:text-white">
                      Contacto General
                    </TabsTrigger>
                    <TabsTrigger value="cotizacion" className="data-[state=active]:bg-green-700 data-[state=active]:text-white">
                      Solicitar Cotización
                    </TabsTrigger>
                  </TabsList>

                  {/* Contact Form */}
                  <TabsContent value="contacto">
                    <form onSubmit={handleContactSubmit} className="space-y-4">
                      <div>
                        <Label htmlFor="contact-name">Nombre completo *</Label>
                        <Input
                          id="contact-name"
                          value={contactForm.name}
                          onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                          required
                          placeholder="Tu nombre"
                          className="mt-1"
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="contact-email">Email *</Label>
                          <Input
                            id="contact-email"
                            type="email"
                            value={contactForm.email}
                            onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                            required
                            placeholder="tu@email.com"
                            className="mt-1"
                          />
                        </div>

                        <div>
                          <Label htmlFor="contact-phone">Teléfono *</Label>
                          <Input
                            id="contact-phone"
                            type="tel"
                            value={contactForm.phone}
                            onChange={(e) => setContactForm({...contactForm, phone: e.target.value})}
                            required
                            placeholder="+52 81 1234 5678"
                            className="mt-1"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="contact-message">Mensaje *</Label>
                        <Textarea
                          id="contact-message"
                          value={contactForm.message}
                          onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
                          required
                          placeholder="Cuéntanos cómo podemos ayudarte..."
                          rows={5}
                          className="mt-1"
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full bg-green-700 hover:bg-green-800 text-white py-6"
                      >
                        Enviar por WhatsApp
                        <Send className="w-4 h-4 ml-2" />
                      </Button>
                    </form>
                  </TabsContent>

                  {/* Quote Form */}
                  <TabsContent value="cotizacion">
                    <form onSubmit={handleQuoteSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="quote-name">Nombre completo *</Label>
                          <Input
                            id="quote-name"
                            value={quoteForm.name}
                            onChange={(e) => setQuoteForm({...quoteForm, name: e.target.value})}
                            required
                            placeholder="Tu nombre"
                            className="mt-1"
                          />
                        </div>

                        <div>
                          <Label htmlFor="quote-company">Empresa</Label>
                          <Input
                            id="quote-company"
                            value={quoteForm.company}
                            onChange={(e) => setQuoteForm({...quoteForm, company: e.target.value})}
                            placeholder="Nombre de tu empresa"
                            className="mt-1"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="quote-email">Email *</Label>
                          <Input
                            id="quote-email"
                            type="email"
                            value={quoteForm.email}
                            onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})}
                            required
                            placeholder="tu@email.com"
                            className="mt-1"
                          />
                        </div>

                        <div>
                          <Label htmlFor="quote-phone">Teléfono *</Label>
                          <Input
                            id="quote-phone"
                            type="tel"
                            value={quoteForm.phone}
                            onChange={(e) => setQuoteForm({...quoteForm, phone: e.target.value})}
                            required
                            placeholder="+52 81 1234 5678"
                            className="mt-1"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="quote-product">Tipo de producto *</Label>
                          <Input
                            id="quote-product"
                            value={quoteForm.productType}
                            onChange={(e) => setQuoteForm({...quoteForm, productType: e.target.value})}
                            required
                            placeholder="Ej: Tarima reciclada 100x120"
                            className="mt-1"
                          />
                        </div>

                        <div>
                          <Label htmlFor="quote-quantity">Cantidad estimada *</Label>
                          <Input
                            id="quote-quantity"
                            value={quoteForm.quantity}
                            onChange={(e) => setQuoteForm({...quoteForm, quantity: e.target.value})}
                            required
                            placeholder="Ej: 100 piezas"
                            className="mt-1"
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="quote-description">Descripción y necesidades especiales</Label>
                        <Textarea
                          id="quote-description"
                          value={quoteForm.description}
                          onChange={(e) => setQuoteForm({...quoteForm, description: e.target.value})}
                          placeholder="Cuéntanos más sobre tus necesidades: tratamiento HT, medidas especiales, frecuencia de pedidos, etc."
                          rows={4}
                          className="mt-1"
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full bg-green-700 hover:bg-green-800 text-white py-6"
                      >
                        Solicitar Cotización por WhatsApp
                        <Send className="w-4 h-4 ml-2" />
                      </Button>
                    </form>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
};
